import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Reveal } from "@/components/motion/Reveal";
import { useMedia } from "@/components/MediaViewer";
import { ArrowUpLeft } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import { imgSrc, money } from "@/lib/api";
import OptionGroup, { OPTION_ORDER, optionLabel } from "@/components/kingsley/OptionGroup";
import PieceRequest from "@/components/kingsley/PieceRequest";

const MEASURES = ["bust", "waist", "hips", "height"];
const mcls = "w-full border-b border-ink/25 bg-transparent pb-3 pt-1 font-display text-lg font-extralight text-ink outline-none placeholder:text-muted/80 focus:border-ink";

function PieceImages({ product }) {
  const { open } = useMedia();
  const items = product.images.map((s) => ({ type: "image", src: imgSrc(s), alt: product.name }));
  return (
    <div className="lg:col-span-7">
      <button type="button" data-testid="kingsley-piece-image" onClick={() => open({ items, index: 0 })} className="group block w-full overflow-hidden bg-[#333336]">
        <img src={imgSrc(product.images[0])} alt={product.name} className="hover-media w-full object-cover" />
      </button>
      {product.images.length > 1 && (
        <div className="mt-4 grid grid-cols-4 gap-4">
          {product.images.map((s, i) => (
            <button key={s + i} type="button" onClick={() => open({ items, index: i })} data-testid={`kingsley-piece-thumb-${i}`} className="group block overflow-hidden bg-[#333336]">
              <img src={imgSrc(s)} alt={`${product.name} view ${i + 1}`} className="hover-media aspect-square w-full object-cover object-top" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Measurements({ m, setM }) {
  return (
    <div data-testid="kingsley-measurements">
      <span className="overline mb-3 block text-muted">Your measurements <span className="text-muted">· optional, helps me cut to your shape</span></span>
      <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
        {MEASURES.map((k) => (
          <input key={k} data-testid={`reserve-${k}`} value={m[k] || ""} onChange={(e) => setM({ ...m, [k]: e.target.value })} placeholder={k[0].toUpperCase() + k.slice(1)} className={mcls} />
        ))}
      </div>
    </div>
  );
}

export default function KingsleyPiece() {
  const { productId } = useParams();
  const { product: getProduct, loading } = useStore();
  const { add } = useCart();
  const product = getProduct(productId);
  const [sel, setSel] = useState({});
  const [m, setM] = useState({});

  if (loading) return <div className="min-h-screen bg-[#EDECE9]" />;
  if (!product) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-[#EDECE9] px-6 text-center">
        <h1 className="font-display text-3xl font-extralight text-ink">Piece not found.</h1>
        <Link to="/kingsley" className="link-underline mt-6 overline text-stone">Back to the boutique</Link>
      </div>
    );
  }

  const isRTW = product.kind === "ready-to-wear";
  const opts = product.options || {};
  const optionKeys = [...OPTION_ORDER.filter((k) => opts[k]?.length), ...Object.keys(opts).filter((k) => !OPTION_ORDER.includes(k) && opts[k]?.length)];
  const measures = !isRTW && MEASURES.some((k) => m[k]) ? `Measurements — ${MEASURES.map((k) => `${k}: ${m[k] || "—"}`).join(", ")}` : "";
  const buyable = product.price != null;
  const soldOut = product.stock === 0;

  const addToBag = () => {
    const missing = optionKeys.filter((k) => opts[k].length > 1 && !sel[k]);
    if (missing.length) return toast.error(`Please choose your ${missing.map(optionLabel).join(", ").toLowerCase()}.`);
    const chosen = Object.fromEntries(optionKeys.map((k) => [k, sel[k] || opts[k][0]]));
    add({ product_id: product.id, name: product.name, price: product.price, image: product.images[0], qty: 1, options: chosen, details: measures });
  };

  return (
    <div className="min-h-screen bg-[#EDECE9] px-6 pb-28 pt-32 md:px-12 md:pt-40">
      <div className="mx-auto max-w-[1500px]">
        <Link to={`/kingsley/c/${product.category}`} data-testid="kingsley-back-category" className="group inline-flex items-center gap-2 text-stone transition-colors hover:text-ink">
          <ArrowUpLeft className="h-4 w-4 transition-transform duration-500 group-hover:-translate-x-0.5" />
          <span className="overline">Back</span>
        </Link>
        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <PieceImages product={product} />
          <div className="lg:col-span-5">
            <Reveal>
              {isRTW ? (
                <span className="rounded-full border border-clay/40 px-3 py-1 font-mono text-[0.55rem] uppercase tracking-widest text-clay">Ready to wear</span>
              ) : (
                <span className="overline text-clay">Made for you</span>
              )}
              <h1 className="mt-5 font-display text-5xl font-extralight tracking-tight text-ink md:text-6xl">{product.name}</h1>
              <p data-testid="kingsley-piece-price" className="mt-4 font-display text-2xl font-extralight text-ink">{soldOut ? "Sold" : money(product.price)}</p>
              <p className="overline mt-3 text-stone">{soldOut ? "Sold — this one has found its home" : product.lead_time}</p>
              {product.one_of_a_kind && <p data-testid="kingsley-piece-one-of-a-kind" className="overline mt-2 text-clay">One of a kind · made by hand by Phaedra</p>}
              <p className="mt-6 max-w-md whitespace-pre-line font-body text-sm leading-relaxed text-stone md:text-base">{product.blurb}</p>
            </Reveal>

            <div className="mt-10 flex items-baseline justify-between gap-4 border-t border-hairline pt-8">
              <h2 className="font-display text-2xl font-extralight tracking-tight text-ink">{buyable ? "Make it yours" : "Order by request"}</h2>
              <Link to="/kingsley/fitting" data-testid="kingsley-piece-fitting-link" className="link-underline overline text-clay">or book a fitting</Link>
            </div>

            <div className="mt-8 flex flex-col gap-8">
              {optionKeys.map((k) => (
                <OptionGroup key={k} label={optionLabel(k)} values={opts[k]} selected={sel[k]} onSelect={(v) => setSel((s) => ({ ...s, [k]: v }))} testid={`kingsley-opt-${k}`} />
              ))}
              {!isRTW && <Measurements m={m} setM={setM} />}
              {buyable ? (
                <div>
                  <button type="button" data-testid="kingsley-add-to-bag" onClick={addToBag} disabled={soldOut}
                    className="group inline-flex w-full items-center justify-center gap-3 bg-ink px-10 py-4 text-paper transition-colors duration-500 hover:bg-ink/85 disabled:opacity-40 sm:w-fit">
                    <span className="overline">{soldOut ? "Sold" : "Add to bag"}</span>
                    {!soldOut && <span className="transition-transform duration-500 group-hover:translate-x-1.5">↗</span>}
                  </button>
                  <p className="mt-4 font-body text-xs leading-relaxed text-stone">
                    {soldOut ? <>Love it? <Link to="/kingsley/fitting" className="link-underline">Ask about something similar</Link>.</> : "Secure checkout. Shipped by Phaedra from the studio."}
                  </p>
                </div>
              ) : (
                <PieceRequest product={product} sel={sel} optionKeys={optionKeys} measures={measures} onDone={() => { setSel({}); setM({}); }} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
